import Table from "components/DataTable/Table";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Modal from "react-bootstrap/Modal";
import { Link } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileExcel,
  faFileArchive,
  faFilePdf,
  faFileText,
  faFileWord,
  faFileZipper,
  faFileImage,
  faCircleQuestion,
} from "@fortawesome/free-solid-svg-icons";
import { faFileImage as faFileImageRegular } from "@fortawesome/free-regular-svg-icons";

function LeadFiles({ selectedLead }) {
  const user = useSelector((state) => state.user?.userInfo);
  const dispatch = useDispatch();

  const [leadFiles, setLeadFiles] = useState([]);
  const [filterBy, setFilterBy] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePayload, setDeletePayload] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadedIdList, setUploadedIdList] = useState([]);
  const [lastSortId, setLastSortId] = useState(0);
  const [newFileName, setNewFileName] = useState("");
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const entityId = useMemo(() => selectedLead?.id, [selectedLead]);

  const canManageFiles = useMemo(
    () => user?.roles === "admin" || user?.permissions?.includes("lead_update"),
    [user]
  );

  const icons = {
    xlsx: faFileExcel,
    xls: faFileExcel,
    docx: faFileWord,
    doc: faFileWord,
    pdf: faFilePdf,
    png: faFileImage,
    jpg: faFileImageRegular,
    jpeg: faFileImageRegular,
    txt: faFileText,
    zip: faFileZipper,
    rar: faFileArchive,
  };

  const getLeadFiles = useCallback(async () => {
    if (!entityId) {
      setLeadFiles([]);
      return;
    }

    try {
      setLoading(true);
      const { data } = await API.post(
        "/lead-file/listfiles",
        { id: entityId },
        { headers: authHeader() }
      );

      if (Array.isArray(data)) {
        setLeadFiles(data);
      } else if (Array.isArray(data?.files)) {
        setLeadFiles(data.files);
      } else {
        setLeadFiles([]);
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch, entityId]);

  const handleFileChange = (event) => {
    const allFiles = Array.from(event.target.files || []).map(
      (file, index) => ({
        id: index,
        file,
      })
    );
    setPendingFiles(allFiles);
  };

  const uploadFile = async (fileWrapper, sortId) => {
    if (!fileWrapper?.file) {
      dispatch(
        setNotification({
          message: "No file selected for upload.",
          type: "error",
        })
      );
      return;
    }

    const formData = new FormData();
    formData.append("file", fileWrapper.file);
    formData.append("folder", `kms/lead/${entityId}/attachments`);
    formData.append("sortId", sortId + 1);
    formData.append("uploadedBy", user?.id);
    formData.append("companyId", user?.companyId);
    formData.append("leadId", entityId);

    await API.post("/lead-file/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    setUploadedIdList((prev) => prev.concat(fileWrapper.id));
  };

  const handleUploadFiles = async (event) => {
    event.preventDefault();
    if (!pendingFiles?.length) {
      setShowUploadModal(false);
      return;
    }

    try {
      setLoading(true);
      const baseSortId = Number.isFinite(lastSortId) ? lastSortId : 0;
      await Promise.all(
        pendingFiles.map((file, index) => uploadFile(file, baseSortId + index))
      );

      dispatch(
        setNotification({
          message: "File uploaded successfully",
          type: "success",
        })
      );
      setPendingFiles([]);
      setUploadedIdList([]);
      setLastSortId(0);
      setShowUploadModal(false);
      await getLeadFiles();
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (fileKey) => {
    if (!fileKey) return;

    try {
      setLoading(true);
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URI}/aws/images`,
        { key: fileKey }
      );
      const url = data?.[0];
      if (!url) return;

      const link = document.createElement("a");
      link.href = url;
      // Opens in a new tab (user, 2026-11-02) instead of downloading.
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const deleteFile = async ({ id, file }) => {
    if (!id || !file) return;

    try {
      setLoading(true);
      await API.post(
        "/lead-file/delete-file",
        { id, file },
        { headers: authHeader() }
      );
      dispatch(
        setNotification({
          message: "Deleted successfully",
          type: "success",
        })
      );
      setShowDeleteModal(false);
      await getLeadFiles();
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const getFileName = (fileName) => {
    if (!fileName) return "";
    const parts = fileName.split("/");
    return parts[parts.length - 1] || "";
  };

  const fileNameDisplay = (fileName) => {
    const rawName = getFileName(fileName);
    if (!rawName) return "";

    const parts = rawName.split(".");
    const extension = parts.length > 1 ? parts.pop() : "";
    const baseName = parts.join(".");

    if (baseName.length > 50) {
      return `${baseName.slice(0, 50)}...${extension}`;
    }

    return rawName;
  };

  const getFileExt = (fileName) => {
    const rawName = getFileName(fileName);
    if (!rawName || !rawName.includes(".")) return "";
    const ext = rawName.split(".").pop();
    return ext ? ext.toLowerCase() : "";
  };

  const handleRenameFile = async () => {
    const originalPath = selectedFile?.fileName || selectedFile?.file;
    if (!originalPath || !newFileName) return;

    try {
      setLoading(true);
      const fileOnlyName = getFileName(originalPath);
      const basePath = originalPath.slice(
        0,
        originalPath.indexOf(fileOnlyName)
      );
      const extension = fileOnlyName.includes(".")
        ? `.${fileOnlyName.split(".").pop()}`
        : "";
      const updatedFileName = `${basePath}${newFileName}${extension}`;

      await API.post(
        "/lead-file/rename",
        {
          oldFileName: originalPath,
          newFileName: updatedFileName,
          id: selectedFile?.id,
        },
        { headers: authHeader() }
      );

      dispatch(
        setNotification({
          message: "File renamed successfully",
          type: "success",
        })
      );
      setShowRenameModal(false);
      setSelectedFile(null);
      await getLeadFiles();
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const sortUp = async (index) => {
    if (!leadFiles?.length || index <= 0) return;

    const current = leadFiles[index];
    const previous = leadFiles[index - 1];
    if (!current?.sortId || !previous?.sortId) return;

    try {
      setLoading(true);
      const { data } = await API.post(
        "/lead-file/sortup",
        {
          currentSortId: current.sortId,
          currentId: current.id,
          upSortId: previous.sortId,
          upId: previous.id,
        },
        { headers: authHeader() }
      );
      if (data?.success) {
        await getLeadFiles();
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const sortDown = async (index) => {
    if (!leadFiles?.length || index >= leadFiles.length - 1) return;

    const current = leadFiles[index];
    const next = leadFiles[index + 1];
    if (!current?.sortId || !next?.sortId) return;

    try {
      setLoading(true);
      const { data } = await API.post(
        "/lead-file/sortDown",
        {
          currentSortId: current.sortId,
          currentId: current.id,
          downSortId: next.sortId,
          downId: next.id,
        },
        { headers: authHeader() }
      );
      if (data?.success) {
        await getLeadFiles();
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const convertSqlTimestampToDate = (timestamp) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      getLeadFiles();
    }, 100);

    return () => clearTimeout(timeout);
  }, [getLeadFiles]);

  useEffect(() => {
    if (leadFiles?.length) {
      const lastItem = leadFiles[leadFiles.length - 1];
      setLastSortId(lastItem?.sortId || 0);
    } else {
      setLastSortId(0);
    }
  }, [leadFiles]);

  useEffect(() => {
    if (!selectedFile) {
      setNewFileName("");
      return;
    }

    const fileName = getFileName(selectedFile?.fileName || selectedFile?.file);
    if (!fileName) {
      setNewFileName("");
      return;
    }

    if (!fileName.includes(".")) {
      setNewFileName(fileName);
      return;
    }

    const parts = fileName.split(".");
    parts.pop();
    setNewFileName(parts.join("."));
  }, [selectedFile]);

  useEffect(() => {
    if (!uploadedIdList.length) return;
    setPendingFiles((prev) =>
      prev.filter((item) => !uploadedIdList.includes(item.id))
    );
  }, [uploadedIdList]);

  const btn1 = () => (
    <div>
      <button
        onClick={() => setShowUploadModal(true)}
        type="button"
        className="btn btn-primary ms-1"
        disabled={!canManageFiles}
      >
        Add FILES
      </button>
    </div>
  );

  const buttons = () => (
    <div className="d-flex justify-content-end">
      <button
        type="button"
        className="px-1 bg-primary btn text-dark bg-opacity-10 border border-primary rounded-end mx-2 rounded-start"
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        {filterBy
          ? filterBy.toUpperCase().replaceAll("_", " ")
          : "Filter by All"}
      </button>
      <div className="dropdown-menu">
        <button
          onClick={() => setFilterBy(null)}
          className="dropdown-item"
          type="button"
        >
          Filter by All
        </button>
      </div>
    </div>
  );

  const table_head = ["File Name", "Last Updated", "Uploaded By", "Actions"];

  const table_body = (item, index) => {
    const fileKey = item?.fileName || item?.file;
    const uploadedBy = item?.uploadedByName || item?.uploadedBy || "-";
    const lastUpdated = item?.updatedAt || item?.createdAt;
    const icon = icons[getFileExt(fileKey)] || faCircleQuestion;

    return (
      <tr key={item?.id || `${fileKey}-${index}`}>
        <td
          className="d-flex align-items-center gap-2"
          style={{ width: "100%" }}
        >
          <span>
            <FontAwesomeIcon style={{ fontSize: 25 }} icon={icon} />
          </span>
          <span>{fileNameDisplay(fileKey)}</span>
        </td>
        <td className="w-0">{convertSqlTimestampToDate(lastUpdated)}</td>
        <td className="w-0">{uploadedBy}</td>
        <td className="w-0">
          {user?.roles === "admin" && (
            <Link
              onClick={() => {
                if (item?.sortId !== 1) {
                  sortUp(index);
                }
              }}
              className="action-icon"
            >
              <i className="mdi mdi-chevron-double-up"></i>
            </Link>
          )}
          {user?.roles === "admin" && (
            <div
              role="button"
              onClick={() => {
                if (item?.sortId !== leadFiles?.length) {
                  sortDown(index);
                }
              }}
              className="action-icon"
            >
              <i className="mdi mdi-chevron-double-down"></i>
            </div>
          )}
          {canManageFiles && (
            <Link
              onClick={() => {
                setSelectedFile(item);
                setShowRenameModal(true);
              }}
              className="btn btn-success btn-sm"
            >
              RN
            </Link>
          )}
          <button
            className="btn btn-success btn-sm px-1 mx-1"
            onClick={() => downloadFile(fileKey)}
          >
            Download
          </button>
          {canManageFiles && (
            <Link
              onClick={() => {
                setShowDeleteModal(true);
                setDeletePayload({ id: item?.id, file: fileKey });
              }}
              className="action-icon"
            >
              <i className="mdi mdi-delete"></i>
            </Link>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="flex-grow-1 py-4">
      {!loading ? (
        <div
          className="px-2 flex-grow-1 d-flex"
          style={{ minHeight: "50vh", borderRadius: 15 }}
        >
          <div className="horizontal-scrollable d-flex flex-grow-1">
            <Table
              filterBy={filterBy}
              buttons={buttons}
              btn1={btn1}
              table_head={table_head}
              table_body={table_body}
              table_data={leadFiles}
            />
          </div>
        </div>
      ) : (
        <Spinner />
      )}

      <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)}>
        <Modal.Body
          className="shadow bg-white px-3"
          style={{ borderRadius: 15 }}
        >
          <div>
            <h4>Add FILES</h4>
          </div>
          <hr className="mt-0" />
          <form onSubmit={handleUploadFiles}>
            <div className="row mb-1">
              <div className="col-12">
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="form-control"
                />
              </div>
            </div>

            <div className="py-2">
              {pendingFiles?.map((file) => (
                <div
                  key={file.id}
                  className="bg-white px-2 rounded mb-2 d-flex justify-content-between align-items-center"
                  style={{
                    boxShadow:
                      "rgba(50, 50, 93, 0.25) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px",
                  }}
                >
                  <div>{file.file.name}</div>
                  <i
                    onClick={() =>
                      setPendingFiles((prev) =>
                        prev.filter((item) => item.id !== file.id)
                      )
                    }
                    className="ri-close-circle-fill text-danger fs-3"
                  ></i>
                </div>
              ))}
            </div>

            <div className="my-2">
              <div className="d-flex justify-content-end">
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setPendingFiles([]);
                    setUploadedIdList([]);
                    setLastSortId(0);
                    setLoading(false);
                  }}
                  className="btn btn-secondary btn-sm"
                  type="button"
                >
                  Close
                </button>
                <button
                  disabled={loading}
                  className="btn btn-primary btn-sm ms-1"
                  type="submit"
                >
                  Add
                </button>
              </div>
            </div>
          </form>
          {loading && <Spinner />}
        </Modal.Body>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Body className="bg-danger">
          <div className="text-center text-white">
            <i className="ri-close-circle-line h1"></i>
            <h4 className="mt-2">Confirm Delete!</h4>
            <p className="mt-3">Do You want to delete ?</p>
            <button
              type="button"
              onClick={() => setShowDeleteModal(false)}
              className="btn btn-light my-2 mx-2"
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-outline-light my-2"
              onClick={() => deleteFile(deletePayload || {})}
            >
              DELETE
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={showRenameModal}
        contentClassName="bg-transparent"
        onHide={() => setShowRenameModal(false)}
      >
        <Modal.Body className="bg-white custom-border-radius">
          <div className="px-2">
            <h4>Rename File</h4>
            <h5 className="my-2">
              {getFileName(selectedFile?.fileName || selectedFile?.file)}
            </h5>
            <input
              onChange={(e) => setNewFileName(e.target.value)}
              value={newFileName}
              placeholder="Enter new filename"
              className="form-control"
            />
            <div className="my-2">
              <div className="d-flex justify-content-end">
                <button
                  onClick={() => {
                    setShowRenameModal(false);
                    setSelectedFile(null);
                  }}
                  className="btn btn-secondary btn-sm"
                  type="button"
                >
                  Close
                </button>
                <button
                  onClick={handleRenameFile}
                  className="btn btn-primary btn-sm ms-1"
                  type="button"
                  disabled={!newFileName}
                >
                  Rename
                </button>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default LeadFiles;
